import { Grid, Stack } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FarmerLayout } from '../../components/farmer-layout/farmer-layout';
import { contractsApi } from '../../lib/apis/contracts';
import { useContractsQueries } from '../../lib/queries/contracts';

import { ConsentStep } from './components/consent-step';
import { FarmerInfo } from './components/farmer-info';
import { FormSkeleton } from './components/form-skeleton';
import { PerFieldStep } from './components/per-field-step';
import { SuccessCard } from './components/success-card';
import { ValidationModeChoice } from './components/validation-mode-choice';
import {
  toPerFieldBulkPayload,
  toSkipAllOrganicPayload,
} from './utils/bulk-submit';
import {
  canSkipAllOrganicAnswers,
  mapFieldAnswersToForm,
} from './utils/map-field-answers';
import { openSkipConfirmModal } from './utils/open-skip-confirm-modal';
import {
  areAllFieldAnswersComplete,
  isFieldAnswerComplete,
  useValidationForm,
  validateFieldAnswerAtIndex,
} from './utils/validation-form';

import { FORM_STATUS } from '@data-access';

type ValidationMode = 'choice' | 'per-field';

export const FarmerValidationPage: FC = () => {
  const { t } = useTranslation();
  const { token } = useSearch({
    from: '/farmer-validation',
  });
  const { ref, width } = useElementSize();

  const [validationMode, setValidationMode] = useState<ValidationMode>('choice');
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [progressValue, setProgressValue] = useState(5);
  const [processCompleted, setProcessCompleted] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(true);

  const form = useValidationForm(t('form.required'));
  const queries = useContractsQueries();
  const info = useQuery(queries.validationInfo(token));

  const confirmInfo = useMutation({
    mutationFn: contractsApi.validation.signValidationBulk,
  });
  const signConsent = useMutation({
    mutationFn: contractsApi.validation.signConsent,
    onSuccess: () => {
      info.refetch();
    },
  });

  const fieldAnswers = info.data?.field_answers ?? [];
  const fieldsCount = fieldAnswers.length;

  const canSkipAllOrganic = useMemo(
    () => canSkipAllOrganicAnswers(fieldAnswers, form.values.fields),
    [fieldAnswers, form.values.fields],
  );

  const currentField = fieldAnswers[currentFieldIndex];
  const formFields = form.values.fields;
  const currentFieldValues = formFields[currentFieldIndex];
  const isCurrentFieldComplete = isFieldAnswerComplete(currentFieldValues);
  const isAllFieldsComplete = areAllFieldAnswersComplete(formFields);
  const hasMultipleFields = fieldsCount > 1;

  const submitBulkAnswers = useCallback(
    async (answers: ReturnType<typeof toSkipAllOrganicPayload>) => {
      try {
        await confirmInfo.mutateAsync({ token, answers });
        setProcessCompleted(true);
      } catch {
        notifications.show({
          title: t('common.ERROR'),
          message: t('farmer-validation.submit-error'),
          color: 'red',
        });
      }
    },
    [confirmInfo, token, t],
  );

  const submitSkipAllOrganic = useCallback(async () => {
    if (!form.values.fields.length) {
      return;
    }

    await submitBulkAnswers(toSkipAllOrganicPayload(form.values.fields));
  }, [form.values.fields, submitBulkAnswers]);

  const handleOpenSkipConfirm = useCallback(() => {
    openSkipConfirmModal({
      count: form.values.fields.length,
      isPending: confirmInfo.isPending,
      onConfirm: submitSkipAllOrganic,
      t,
    });
  }, [
    confirmInfo.isPending,
    form.values.fields.length,
    submitSkipAllOrganic,
    t,
  ]);

  const handleConfirm = useCallback(async () => {
    if (!isAllFieldsComplete) {
      await Promise.all(
        form.values.fields.map((_, index) =>
          validateFieldAnswerAtIndex(form, index),
        ),
      );
      return;
    }

    await submitBulkAnswers(toPerFieldBulkPayload(form.values.fields));
  }, [form, form.values.fields, isAllFieldsComplete, submitBulkAnswers]);

  const handleSelectPerField = () => {
    setValidationMode('per-field');
    setCurrentFieldIndex(0);
    setProgressValue(5);
    setInfoExpanded(false);
  };

  const handleBackToChoice = () => {
    setValidationMode('choice');
    setCurrentFieldIndex(0);
    setProgressValue(5);
    setInfoExpanded(true);
    form.reset();
  };

  useEffect(() => {
    if (!info.data?.field_answers) {
      return;
    }

    const fields = mapFieldAnswersToForm(info.data.field_answers);
    form.setInitialValues({ fields });
    form.reset();
    setCurrentFieldIndex(0);
    setValidationMode('choice');
    setInfoExpanded(true);

    setProcessCompleted(info.data.status === FORM_STATUS.FINISHED);
  }, [info.data]);

  const needsConsent = info.data && !info.data.consent_form?.is_signed;

  if (needsConsent) {
    return (
      <ConsentStep
        consentChecked={isConsentChecked}
        containerRef={ref}
        containerWidth={width}
        info={info}
        onConsentCheckedChange={setIsConsentChecked}
        signConsent={signConsent}
        token={token}
      />
    );
  }

  return (
    <FarmerLayout>
      <Grid w="100%">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <FarmerInfo
            collapsible={!processCompleted && validationMode === 'per-field'}
            expanded={infoExpanded}
            farmName={info.data?.farm_name ?? ''}
            onExpandedChange={setInfoExpanded}
            organizationName={info.data?.organization_name ?? ''}
            projectName={info.data?.project_name ?? ''}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 8 }}>
          {!processCompleted ? (
            <Stack>
              {validationMode === 'choice' ? (
                form.values.fields.length > 0 ? (
                  <ValidationModeChoice
                    canSkipAllOrganic={canSkipAllOrganic}
                    fieldsCount={fieldsCount}
                    isPending={confirmInfo.isPending}
                    onNo={handleOpenSkipConfirm}
                    onYes={handleSelectPerField}
                  />
                ) : (
                  <FormSkeleton />
                )
              ) : form.values.fields.length > 0 ? (
                <PerFieldStep
                  currentField={currentField}
                  currentFieldIndex={currentFieldIndex}
                  fieldsCount={fieldsCount}
                  form={form}
                  hasMultipleFields={hasMultipleFields}
                  isCurrentFieldComplete={isCurrentFieldComplete}
                  isFormComplete={isAllFieldsComplete}
                  isPending={confirmInfo.isPending}
                  onBackToChoice={handleBackToChoice}
                  onConfirm={handleConfirm}
                  progressValue={progressValue}
                  setCurrentFieldIndex={setCurrentFieldIndex}
                  setProgressValue={setProgressValue}
                />
              ) : (
                <FormSkeleton />
              )}
            </Stack>
          ) : (
            <SuccessCard />
          )}
        </Grid.Col>
      </Grid>
    </FarmerLayout>
  );
};
